import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from "react";
import { useApi } from "~hooks/apiHooks/useApi";
import { useGetLocationParams } from "~hooks/useGetLocationParams.ts";
import type { Notification } from "~types/notification.ts";
import type { UserUpdates, UserUpdatesResponse } from "~types/userUpdates.ts";

interface UserUpdatesContextType {
  unreadNotificationCount: number;
  latestNotifications: Notification[];
  catalogLastInvalidateDateTimeUtc: string;
  refetch: () => void;
}

const USER_UPDATES_TIMEOUT = 15000; // 15 seconds

const UserUpdatesContext = createContext<UserUpdatesContextType | undefined>(undefined);

export const UserUpdatesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth0();
  const request = useApi();
  const queryClient = useQueryClient();
  const catalogLastInvalidateDateTimeUtcRef = useRef<string>("");
  const { sport = "", organizationId = "" } = useGetLocationParams();

  // Reset when sport/org changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset ref only on sport/org change
  useEffect(() => {
    catalogLastInvalidateDateTimeUtcRef.current = "";
  }, [sport, organizationId]);

  // Define fetch user updates function
  const fetchUserUpdates = useCallback(async () => {
    const params = new URLSearchParams({
      sport,
      organizationId,
      ...(catalogLastInvalidateDateTimeUtcRef.current && {
        clientCatalogLastInvalidateDateTimeUtc: catalogLastInvalidateDateTimeUtcRef.current,
      }),
    });
    const url = `user/updates?${params.toString()}`;
    const response = await request<UserUpdatesResponse>(url);
    return response?.data?.[0] || ({} as UserUpdates);
  }, [request, sport, organizationId]);

  // Create a query to fetch user updates with refetching every 15 seconds and on window focus
  const { data: userUpdates, refetch } = useQuery<UserUpdates>({
    queryKey: ["user", "updates", sport, organizationId],
    queryFn: () => fetchUserUpdates(),
    staleTime: 0,
    refetchInterval: USER_UPDATES_TIMEOUT,
    refetchOnWindowFocus: true,
    enabled: isAuthenticated,
  });

  // Extract values from last user updates response or set defaults
  const unreadNotificationCount = userUpdates?.unreadNotificationCount || 0;
  const latestNotifications = userUpdates?.latestNotifications || [];
  const catalogLastInvalidateDateTimeUtc = userUpdates?.catalogLastInvalidateDateTimeUtc || "";

  // Update the ref whenever we get a new catalogLastInvalidateDateTimeUtc
  useEffect(() => {
    if (catalogLastInvalidateDateTimeUtc) {
      catalogLastInvalidateDateTimeUtcRef.current = catalogLastInvalidateDateTimeUtc;
    }
  }, [catalogLastInvalidateDateTimeUtc]);

  // Perform invalidation if user scope invalidation is required and do it only one for the same set of parameter values
  useEffect(() => {
    if (userUpdates?.isUserScopeInvalidationRequired) {
      queryClient.invalidateQueries({ queryKey: ["permission-scope-current-user", sport, organizationId] });
      queryClient.invalidateQueries({ queryKey: ["permission-scope-to-manage-permissions-current-user", sport, organizationId] });
      console.info("Current user permission scope has been invalidated successfully.");
    }
  }, [userUpdates?.isUserScopeInvalidationRequired, queryClient, sport, organizationId]);

  // Prepare the context value with the current user updates data and refetch function
  const contextValue: UserUpdatesContextType = {
    unreadNotificationCount,
    latestNotifications,
    catalogLastInvalidateDateTimeUtc,
    refetch,
  };

  // Returns the context provider with the current values and refetch function
  return <UserUpdatesContext.Provider value={contextValue}>{children}</UserUpdatesContext.Provider>;
};

export const useUserUpdates = (): UserUpdatesContextType => {
  const context = useContext(UserUpdatesContext);
  if (!context) {
    throw new Error("useUserUpdates must be used within a UserUpdatesContext.Provider");
  }
  return context;
};
